import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft20Regular,
  Save20Regular,
} from "@fluentui/react-icons";
import { PageHeader, PageHeaderTab } from "../../components/PageHeader";
import { getClients, type Client } from "../../clients/clientsApi";
import { getSuppliers, type Supplier } from "../../fornecedores/fornecedoresApi";
import { getSubcontractors, type Subcontractor } from "../../subempreiteiros/subempreiteirosApi";
import {
  ApiError,
  createWorkBudgetVersion,
  getWorkBudgetVersions,
  getWorkById,
  getWorkItemStatuses,
  getWorkOrders,
  getWorkStatuses,
  getWorkSubcontractors,
  updateWork,
  updateWorkOrder,
  updateWorkSubcontractor,
  type Work,
  type WorkBudgetVersion,
  type WorkItemStatus,
  type WorkOrderItem,
  type WorkStatus,
  type WorkSubcontractorItem,
} from "../../obras/obrasApi";
import { getWorkVariationMap } from "../../workVariations/workVariationsApi";
import { WorkOrdersTab } from "./components/WorkOrdersTab";
import { WorkSubcontractorsTab } from "./components/WorkSubcontractorsTab";
import { WorkFinancialDashboardTab } from "./components/WorkFinancialDashboardTab";
import { WorkFinancialDocumentsTab } from "./components/WorkFinancialDocumentsTab";
import { WorkMilestonesTab } from "./components/WorkMilestonesTab";
import { WorkScheduleTab } from "./components/WorkScheduleTab";
import { WorkCostsTab } from "./components/WorkCostsTab";
import { WorkMaterialsTab } from "./components/WorkMaterialsTab";
import { WorkLaborTab } from "./components/WorkLaborTab";
import { WorkCostMapTab } from "./components/WorkCostMapTab";
import { WorkVariationsTab } from "./components/WorkVariationsTab";
import { WorkPostMortemTab } from "./components/WorkPostMortemTab";
import { WorkAfterSalesTab } from "./components/WorkAfterSalesTab";
import { WorkMeetingMinutesTab } from "./components/WorkMeetingMinutesTab";
import { WorkDiaryTab } from "./components/WorkDiaryTab";
import { WorkPhotoArchiveTab } from "./components/WorkPhotoArchiveTab";
import { EntityDocumentsSection } from "../../components/EntityDocumentsSection";
import { WorkMeasurementAutosTab } from "./components/WorkMeasurementAutosTab";
import { WorkSubcontractorAutosTab } from "./components/WorkSubcontractorAutosTab";
import { SECTIONS, sectionForSubTab, type WorkSubTabId } from "./workSections";
import { WorkSubNav } from "./components/WorkSubNav";
import { WorkSummaryDashboard } from "./components/WorkSummaryDashboard";
import { WorkEditDrawer, type WorkEditValues } from "./components/WorkEditDrawer";
import { formatCurrency, formatDateShort } from "./obraFormat";
import {
  Comments,
  type Comment as UiComment,
} from "../../components/Comments";
import {
  createComment,
  deleteComment,
  getCommentAttachmentDownloadUrl,
  getComments,
  updateComment,
  uploadCommentAttachment,
  type Comment as ApiComment,
} from "../../comments/commentsApi";

const CONTRACT_CATEGORIES = [
  "Empreitada",
  "Subempreitada",
  "Fornecimento",
  "Arrendamento",
  "Prestação de serviços",
  "Outro",
];
const TECHNICAL_CATEGORIES = [
  "Planta",
  "Especificação",
  "Fotografia",
  "Ata",
  "Comunicação",
  "Outro",
];

const parseErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar esta obra.";
    }
    if (error.status === 404) {
      return "Obra não encontrada.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const normalizeSystemType = (
  value?: string | null,
):
  | "status_change"
  | "document_upload"
  | "value_update"
  | "other"
  | undefined => {
  if (
    value === "status_change" ||
    value === "document_upload" ||
    value === "value_update" ||
    value === "other"
  ) {
    return value;
  }

  return undefined;
};

const mapApiCommentToUi = (comment: ApiComment): UiComment => {
  return {
    id: comment.id.toString(),
    author: {
      name: comment.author.name,
      avatar: comment.author.avatarUrl ?? undefined,
      isSystem: comment.author.isSystem,
    },
    content: comment.content,
    timestamp: new Date(comment.created),
    isSystem: comment.isSystem,
    systemType: normalizeSystemType(comment.systemType),
    canEdit: comment.canEdit,
    attachments: comment.attachments.map((attachment) => ({
      id: attachment.id.toString(),
      name: attachment.fileName,
      type: attachment.contentType,
      url:
        attachment.url ??
        getCommentAttachmentDownloadUrl(comment.id, attachment.id),
    })),
  };
};

export function EditarObraView() {
  const location = useLocation();
  const navigate = useNavigate();
  const { workId } = useParams();
  const parsedWorkId = workId
    ? Number.parseInt(workId, 10)
    : Number.NaN;

  const [work, setWork] = useState<Work | null>(null);
  const [orders, setOrders] = useState<WorkOrderItem[]>([]);
  const [workSubcontractors, setWorkSubcontractors] = useState<
    WorkSubcontractorItem[]
  >([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState<WorkSubTabId>("resumo");
  const activeSection = useMemo(
    () => sectionForSubTab(activeSubTab),
    [activeSubTab],
  );

  const [clienteId, setClienteId] = useState<number | "">("");
  const [nomeObra, setNomeObra] = useState("");
  const [localObra, setLocalObra] = useState("");
  const [workStatusId, setWorkStatusId] = useState<number | null>(null);
  const [workStatuses, setWorkStatuses] = useState<WorkStatus[]>([]);
  const [itemStatuses, setItemStatuses] = useState<WorkItemStatus[]>([]);
  const [dataInicio, setDataInicio] = useState<string | null>(null);
  const [dataFim, setDataFim] = useState<string | null>(null);
  const [progresso, setProgresso] = useState<number | null>(null);
  const [valorTotal, setValorTotal] = useState<number | null>(null);
  const [valorGasto, setValorGasto] = useState<number | null>(null);
  // [4.8.1] Orçamento revisto (base + variações aprovadas) para o Resumo.
  const [revisedSell, setRevisedSell] = useState<number | null>(null);
  const [baseSell, setBaseSell] = useState<number | null>(null);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isSavingObra, setIsSavingObra] = useState(false);
  const [saveObraError, setSaveObraError] = useState<string | null>(null);
  const [dirtyOrders, setDirtyOrders] = useState<Set<number>>(
    new Set(),
  );
  const [dirtySubcontractors, setDirtySubcontractors] = useState<
    Set<number>
  >(new Set());
  // Bumped on any cost imputation/removal so the Custos tab re-fetches if open.
  const [costsRefreshKey, setCostsRefreshKey] = useState(0);
  const [comments, setComments] = useState<UiComment[]>([]);
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [budgetVersions, setBudgetVersions] = useState<
    WorkBudgetVersion[]
  >([]);
  const [isBudgetVersionsLoading, setIsBudgetVersionsLoading] =
    useState(false);
  const [budgetVersionsError, setBudgetVersionsError] = useState<
    string | null
  >(null);
  const [isCreatingBudgetVersion, setIsCreatingBudgetVersion] =
    useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadWork = async () => {
      if (!Number.isFinite(parsedWorkId)) {
        setLoadError("Obra inválida.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);

      try {
        const [
          workData,
          workOrders,
          subcontractorItems,
          clientList,
          supplierList,
          subcontractorList,
          statusList,
          itemStatusList,
        ] = await Promise.all([
          getWorkById(parsedWorkId),
          getWorkOrders(parsedWorkId),
          getWorkSubcontractors(parsedWorkId),
          getClients(),
          getSuppliers(),
          getSubcontractors(),
          getWorkStatuses(),
          getWorkItemStatuses(),
        ]);

        if (!isMounted) return;

        setWork(workData);
        setOrders(workOrders);
        setWorkSubcontractors(subcontractorItems);
        setClients(clientList);
        setSuppliers(supplierList);
        setSubcontractors(subcontractorList);
        setWorkStatuses(statusList);
        setItemStatuses(itemStatusList);
        setClienteId(workData.clientId);
        setNomeObra(workData.name);
        setLocalObra(workData.location ?? "");
        setWorkStatusId(workData.workStatusId);
        setDataInicio(workData.startDate ?? null);
        setDataFim(workData.endDate ?? null);
        setProgresso(workData.progressPercent ?? 0);
        setValorTotal(workData.totalValue ?? null);
        setValorGasto(workData.spentValue ?? null);
        setDirtyOrders(new Set());
        setDirtySubcontractors(new Set());
      } catch (error) {
        if (!isMounted) return;
        setWork(null);
        setOrders([]);
        setWorkSubcontractors([]);
        setClients([]);
        setSuppliers([]);
        setSubcontractors([]);
        setWorkStatuses([]);
        setItemStatuses([]);
        setLoadError(
          parseErrorMessage(error, "Não foi possível carregar a obra."),
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadWork();

    return () => {
      isMounted = false;
    };
  }, [parsedWorkId]);

  // Mapa de variações (orçamento base → revisto) para o cartão "Valor Adjudicado".
  // Falha silenciosa: é informação suplementar e não deve quebrar a página.
  useEffect(() => {
    if (!Number.isFinite(parsedWorkId)) {
      setRevisedSell(null);
      setBaseSell(null);
      return;
    }

    let active = true;
    getWorkVariationMap(parsedWorkId)
      .then((map) => {
        if (!active) return;
        setRevisedSell(map.revisedSell);
        setBaseSell(map.baseSell);
      })
      .catch(() => {
        if (!active) return;
        setRevisedSell(null);
        setBaseSell(null);
      });

    return () => {
      active = false;
    };
  }, [parsedWorkId]);

  useEffect(() => {
    if (!Number.isFinite(parsedWorkId)) {
      return;
    }

    let isMounted = true;

    const loadComments = async () => {
      setIsCommentsLoading(true);
      setCommentsError(null);

      try {
        const response = await getComments("work", parsedWorkId);
        if (!isMounted) return;
        setComments(response.map(mapApiCommentToUi));
      } catch (error) {
        if (!isMounted) return;
        const message =
          error instanceof Error && error.message
            ? error.message
            : "Não foi possível carregar os comentários.";
        setCommentsError(message);
        setComments([]);
      } finally {
        if (isMounted) {
          setIsCommentsLoading(false);
        }
      }
    };

    loadComments();

    return () => {
      isMounted = false;
    };
  }, [parsedWorkId]);

  const budgetId = work?.budgetId ?? null;

  const loadBudgetVersions = useCallback(async () => {
    if (!budgetId) {
      setBudgetVersions([]);
      return;
    }

    setIsBudgetVersionsLoading(true);
    setBudgetVersionsError(null);

    try {
      const versions = await getWorkBudgetVersions(budgetId);
      setBudgetVersions(versions);
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Não foi possível carregar as versões do orçamento.";
      setBudgetVersionsError(message);
      setBudgetVersions([]);
    } finally {
      setIsBudgetVersionsLoading(false);
    }
  }, [budgetId]);

  useEffect(() => {
    loadBudgetVersions();
  }, [loadBudgetVersions]);

  const handleCreateBudgetVersion = async () => {
    if (!budgetId) return;

    setIsCreatingBudgetVersion(true);
    setBudgetVersionsError(null);

    try {
      const created = await createWorkBudgetVersion(budgetId);
      if (created?.id) {
        // Duplicar uma versão é para a alterar — abre logo a nova revisão em edição.
        navigate(`/orcamentos/editar/${created.id}`);
        return;
      }
      await loadBudgetVersions();
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Não foi possível criar a nova revisão.";
      setBudgetVersionsError(message);
    } finally {
      setIsCreatingBudgetVersion(false);
    }
  };

  const showToast = (message: string) => {
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: { toast: message },
    });
  };

  const resolveWastePercent = (quantity: number, wasteQuantity: number): number => {
    if (quantity <= 0) return 0;
    const effectiveWaste = wasteQuantity > 0 ? wasteQuantity : quantity;
    const percent = ((effectiveWaste / quantity) - 1) * 100;
    return percent > 0 ? percent : 0;
  };

  const applyWastePercent = (quantity: number, wastePercent: number): number => {
    if (quantity <= 0) return 0;
    const percent = Number.isFinite(wastePercent) ? Math.max(0, wastePercent) : 0;
    const result = quantity * (1 + percent / 100);
    return Number(result.toFixed(3));
  };

  const onOrderChange = (
    orderId: number,
    updates: Partial<WorkOrderItem>,
  ) => {
    setOrders((prev) =>
      prev.map((order) => {
        if (order.id !== orderId) {
          return order;
        }

        const next = { ...order, ...updates };
        if (updates.quantity !== undefined && updates.wasteQuantity === undefined) {
          const wastePercent = resolveWastePercent(order.quantity, order.wasteQuantity);
          next.wasteQuantity = applyWastePercent(updates.quantity, wastePercent);
        }

        return next;
      }),
    );
    setDirtyOrders((prev) => {
      const next = new Set(prev);
      next.add(orderId);
      return next;
    });
  };

  const onSubcontractorChange = (
    itemId: number,
    updates: Partial<WorkSubcontractorItem>,
  ) => {
    setWorkSubcontractors((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item,
      ),
    );
    setDirtySubcontractors((prev) => {
      const next = new Set(prev);
      next.add(itemId);
      return next;
    });
  };

  // Replace a subcontractor item by id after a persisted change (impute/anular),
  // WITHOUT marking it dirty — the impute already persisted server-side.
  const onSubcontractorReplaced = (updated: WorkSubcontractorItem) => {
    setWorkSubcontractors((prev) =>
      prev.map((item) => (item.id === updated.id ? updated : item)),
    );
  };

  const onSubcontractorCreated = (created: WorkSubcontractorItem) => {
    setWorkSubcontractors((prev) => [...prev, created]);
  };

  const handleSaveObra = async (values: WorkEditValues) => {
    if (!Number.isFinite(parsedWorkId) || !work) {
      setSaveObraError("Obra inválida.");
      return;
    }
    if (!values.clienteId || values.nomeObra.trim() === "") {
      setSaveObraError("Preencha o cliente e o nome da obra.");
      return;
    }
    setIsSavingObra(true);
    setSaveObraError(null);
    try {
      const updated = await updateWork(parsedWorkId, {
        clientId: values.clienteId as number,
        name: values.nomeObra.trim(),
        location: values.localObra.trim() || null,
        startDate: values.dataInicio,
        endDate: values.dataFim,
        progressPercent: values.progresso ?? 0,
        totalValue: values.valorTotal,
        spentValue: values.valorGasto,
        workStatusId: values.workStatusId ?? work.workStatusId,
      });
      setWork(updated);
      setClienteId(updated.clientId);
      setNomeObra(updated.name);
      setLocalObra(updated.location ?? "");
      setWorkStatusId(updated.workStatusId);
      setDataInicio(updated.startDate ?? null);
      setDataFim(updated.endDate ?? null);
      setProgresso(updated.progressPercent ?? 0);
      setValorTotal(updated.totalValue ?? null);
      setValorGasto(updated.spentValue ?? null);
      setIsEditDrawerOpen(false);
      showToast("Obra atualizada com sucesso.");
    } catch (error) {
      setSaveObraError(parseErrorMessage(error, "Não foi possível guardar a obra."));
    } finally {
      setIsSavingObra(false);
    }
  };

  const handleSave = async () => {
    if (!Number.isFinite(parsedWorkId) || !work) {
      setSaveError("Obra inválida.");
      return;
    }

    if (!clienteId || nomeObra.trim() === "") {
      setSaveError("Preencha o cliente e o nome da obra.");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      if (dirtyOrders.size > 0) {
        const updatedOrders: WorkOrderItem[] = [];

        for (const orderId of Array.from(dirtyOrders)) {
          const order = orders.find((item) => item.id === orderId);
          if (!order) {
            continue;
          }

          const updated = await updateWorkOrder(parsedWorkId, orderId, {
            code: order.code.trim(),
            type: order.type?.trim() || null,
            location: order.location?.trim() || null,
            description: order.description.trim(),
            unit: order.unit.trim(),
            quantity: order.quantity,
            wasteQuantity: order.wasteQuantity,
            workItemStatusId: order.workItemStatusId,
            needDate: order.needDate || null,
            deliveryDate: order.deliveryDate || null,
            deliveryWarehouseId: order.deliveryWarehouseId ?? null,
            receptionResponsible: order.receptionResponsible?.trim() || null,
            deliveryNotes: order.deliveryNotes?.trim() || null,
            unitPrice: order.unitPrice,
            supplier: order.supplier?.trim() || null,
          });

          updatedOrders.push(updated);
        }

        setOrders((prev) =>
          prev.map((order) => {
            const updated = updatedOrders.find(
              (item) => item.id === order.id,
            );
            return updated ? { ...order, ...updated } : order;
          }),
        );

        setDirtyOrders(new Set());
      }

      if (dirtySubcontractors.size > 0) {
        const updatedItems: WorkSubcontractorItem[] = [];

        for (const itemId of Array.from(dirtySubcontractors)) {
          const item = workSubcontractors.find(
            (entry) => entry.id === itemId,
          );
          if (!item) {
            continue;
          }

          const updated = await updateWorkSubcontractor(
            parsedWorkId,
            itemId,
            {
              code: item.code.trim(),
              type: item.type?.trim() || null,
              location: item.location?.trim() || null,
              description: item.description.trim(),
              subcontractor: item.subcontractor.trim(),
              value: item.value,
              workItemStatusId: item.workItemStatusId,
              needDate: item.needDate || null,
            },
          );

          updatedItems.push(updated);
        }

        setWorkSubcontractors((prev) =>
          prev.map((item) => {
            const updated = updatedItems.find(
              (entry) => entry.id === item.id,
            );
            return updated ? { ...item, ...updated } : item;
          }),
        );

        setDirtySubcontractors(new Set());
      }

      showToast("Obra atualizada com sucesso.");
    } catch (error) {
      setSaveError(
        parseErrorMessage(
          error,
          "Não foi possível guardar a obra.",
        ),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdicionarComentario = async (
    content: string,
    attachments?: File[],
  ) => {
    if (!Number.isFinite(parsedWorkId)) return;

    const trimmedContent = content.trim();
    if (!trimmedContent && (!attachments || attachments.length === 0)) {
      alert("Escreva um comentário ou adicione um ficheiro.");
      return;
    }

    try {
      const uploadedAttachments = attachments?.length
        ? await Promise.all(
            attachments.map((file) => uploadCommentAttachment(file)),
          )
        : [];

      const created = await createComment({
        contextType: "work",
        contextId: parsedWorkId,
        content: trimmedContent || "(sem texto)",
        attachments: uploadedAttachments.map((attachment) => ({
          fileName: attachment.fileName,
          contentType:
            attachment.contentType || "application/octet-stream",
          sizeInBytes: attachment.sizeInBytes,
          url: attachment.url ?? undefined,
        })),
      });

      setComments((prev) => [...prev, mapApiCommentToUi(created)]);
    } catch (error) {
      alert("Não foi possível adicionar o comentário.");
    }
  };

  const handleEditarComentario = async (
    commentId: string,
    newContent: string,
  ) => {
    const trimmed = newContent.trim();
    if (!trimmed) return;

    const parsedId = Number.parseInt(commentId, 10);
    if (!Number.isFinite(parsedId)) return;

    try {
      const updated = await updateComment(parsedId, trimmed);
      setComments((prev) =>
        prev.map((comment) =>
          comment.id === commentId
            ? mapApiCommentToUi(updated)
            : comment,
        ),
      );
    } catch (error) {
      alert("Não foi possível atualizar o comentário.");
    }
  };

  const handleApagarComentario = async (commentId: string) => {
    const parsedId = Number.parseInt(commentId, 10);
    if (!Number.isFinite(parsedId)) return;

    try {
      await deleteComment(parsedId);
      setComments((prev) =>
        prev.filter((comment) => comment.id !== commentId),
      );
    } catch (error) {
      alert("Não foi possível remover o comentário.");
    }
  };

  const selectedClientName = useMemo(() => {
    if (clienteId) {
      const selected = clients.find((client) => client.id === clienteId);
      if (selected?.name) {
        return selected.name;
      }
    }

    return work?.clientName ?? "";
  }, [clienteId, clients, work]);

  const headerTitle = nomeObra.trim() || work?.name || "Obra";
  const headerSubtitle = useMemo(() => {
    if (!work) return undefined;

    const parts = [
      selectedClientName,
      work.code ? `Código: ${work.code}` : null,
    ].filter(Boolean);

    return parts.join(" • ") || undefined;
  }, [selectedClientName, work]);

  const statusLabel =
    workStatuses.find((status) => status.id === workStatusId)?.name ??
    work?.statusName ??
    "Sem estado";

  const totalOrders = useMemo(() => {
    return orders.reduce((sum, order) => {
      const quantity =
        order.wasteQuantity > 0 ? order.wasteQuantity : order.quantity;
      return sum + order.unitPrice * quantity;
    }, 0);
  }, [orders]);

  const totalSubcontractors = useMemo(() => {
    return workSubcontractors.reduce(
      (sum, item) => sum + item.value,
      0,
    );
  }, [workSubcontractors]);

  const valorAdjudicado = valorTotal ?? null;
  const inferredTotalCost = totalOrders + totalSubcontractors;
  const custoTotalAtual =
    valorGasto ?? (inferredTotalCost > 0 ? inferredTotalCost : null);
  const margemPrevista = useMemo(() => {
    if (valorAdjudicado === null || custoTotalAtual === null) {
      return null;
    }
    if (valorAdjudicado === 0) {
      return null;
    }

    return (
      ((valorAdjudicado - custoTotalAtual) / valorAdjudicado) *
      100
    );
  }, [valorAdjudicado, custoTotalAtual]);

  const progressoValue = Math.max(
    0,
    Math.min(100, progresso ?? 0),
  );

  const custoMateriais = totalOrders > 0 ? totalOrders : null;
  const custoSubempreiteiros =
    totalSubcontractors > 0 ? totalSubcontractors : null;
  const custoEquipaInterna = null;

  const isFormDirty = dirtyOrders.size > 0 || dirtySubcontractors.size > 0;

  const renderResumo = () => {
    const budgetSlot = work?.budgetId ? (
        <div
          className="bg-card border border-border p-6"
          style={{ borderRadius: "var(--radius-card)" }}
        >
          <div className="flex items-center justify-between mb-5">
            <h3
              className="text-foreground"
              style={{
                fontSize: "var(--text-section-title)",
                fontWeight: "var(--font-weight-semi-bold)",
              }}
            >
              Versões do orçamento
            </h3>
            <button
              type="button"
              onClick={handleCreateBudgetVersion}
              disabled={isCreatingBudgetVersion}
              className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              {isCreatingBudgetVersion ? "A criar..." : "Nova revisão"}
            </button>
          </div>

          <div className="mb-5">
            <p
              className="text-secondary-text mb-1"
              style={{ fontSize: "var(--text-caption)" }}
            >
              Orçamento adjudicado
            </p>
            <button
              type="button"
              onClick={() =>
                navigate(`/orcamentos/editar/${work.budgetId}`)
              }
              className="text-primary hover:underline"
              style={{ fontSize: "var(--text-base)" }}
            >
              {work.budgetReference ?? "—"}
            </button>
          </div>

          {budgetVersionsError && (
            <div
              className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 mb-4"
              style={{ borderRadius: "var(--radius)" }}
            >
              {budgetVersionsError}
            </div>
          )}

          {isBudgetVersionsLoading ? (
            <p className="text-secondary-text text-sm">
              A carregar versões...
            </p>
          ) : budgetVersions.length === 0 ? (
            <p className="text-secondary-text text-sm">
              Sem versões registadas.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted text-left">
                    <th className="px-4 py-3">Nº</th>
                    <th className="px-4 py-3">Etiqueta</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3">Data</th>
                    <th className="px-4 py-3">Autor</th>
                  </tr>
                </thead>
                <tbody>
                  {[...budgetVersions]
                    .sort((a, b) => b.versionNumber - a.versionNumber)
                    .map((version) => (
                    <tr
                      key={version.id}
                      className="border-b border-border last:border-b-0"
                    >
                      <td className="px-4 py-3 align-top">
                        <span className="inline-flex items-center gap-2">
                          {version.versionNumber}
                          {version.isActiveVersion && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary border border-primary/20">
                              Ativa
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top">
                        {version.versionLabel || "—"}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {version.status}
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        {formatCurrency(version.totalAmount)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {formatDateShort(version.created)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {version.createdByName || version.createdBy || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
    ) : null;

    const commentsSlot = (
      <>
        {commentsError && (
          <div
            className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
            style={{ borderRadius: "var(--radius)" }}
          >
            {commentsError}
          </div>
        )}
        <Comments
          comments={comments}
          onAddComment={handleAdicionarComentario}
          onEditComment={handleEditarComentario}
          onDeleteComment={handleApagarComentario}
          isLoading={isCommentsLoading}
        />
      </>
    );

    return (
      <WorkSummaryDashboard
        work={work!}
        clientName={selectedClientName}
        statusLabel={statusLabel}
        valorAdjudicado={valorAdjudicado}
        revisedSell={revisedSell}
        baseSell={baseSell}
        custoTotalAtual={custoTotalAtual}
        margemPrevista={margemPrevista}
        progresso={progressoValue}
        dataInicio={dataInicio}
        dataFim={dataFim}
        custoSubempreiteiros={custoSubempreiteiros}
        custoMateriais={custoMateriais}
        custoEquipaInterna={custoEquipaInterna}
        onEditObra={() => {
          setSaveObraError(null);
          setIsEditDrawerOpen(true);
        }}
        budgetSlot={budgetSlot}
        commentsSlot={commentsSlot}
      />
    );
  };

  return (
    <div className="h-full flex flex-col bg-background">
      <PageHeader
        title={headerTitle}
        subtitle={headerSubtitle}
        titleBadge={
          <div
            className="px-3 py-1.5 bg-accent border border-border rounded flex items-center whitespace-nowrap flex-shrink-0"
            style={{
              borderRadius: "var(--radius)",
              fontSize: "var(--text-caption)",
              fontWeight: "var(--font-weight-semi-bold)",
            }}
          >
            <span
              className="w-2 h-2 rounded-full bg-primary mr-2"
              style={{ borderRadius: "var(--radius-full)" }}
            />
            {statusLabel}
          </div>
        }
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => navigate("/obras")}
              className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              <ArrowLeft20Regular className="w-5 h-5" />
              Voltar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !isFormDirty}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              <Save20Regular className="w-5 h-5" />
              {isSaving ? "A guardar..." : "Guardar alterações"}
            </button>
          </div>
        }
        tabs={
          <>
            {SECTIONS.map((section) => (
              <PageHeaderTab
                key={section.id}
                label={section.label}
                isActive={activeSection.id === section.id}
                onClick={() => setActiveSubTab(section.subItems[0].id)}
              />
            ))}
          </>
        }
      />

      <div className="flex-1 overflow-auto p-4 md:p-6 space-y-6">
        {isLoading && (
          <div
            className="border border-border bg-card px-4 py-3 text-secondary-text"
            style={{ borderRadius: "var(--radius)" }}
          >
            A carregar obra...
          </div>
        )}

        {loadError && (
          <div
            className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
            style={{ borderRadius: "var(--radius)" }}
          >
            {loadError}
          </div>
        )}

        {saveError && (
          <div
            className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
            style={{ borderRadius: "var(--radius)" }}
          >
            {saveError}
          </div>
        )}

        {!isLoading && work && (
          <>
            {activeSection.subItems.length > 1 && (
              <WorkSubNav
                items={activeSection.subItems}
                activeId={activeSubTab}
                onChange={(id) => setActiveSubTab(id as WorkSubTabId)}
              />
            )}
            {activeSubTab === "resumo" && renderResumo()}
            {activeSubTab === "compras" &&
              (orders.length === 0 ? (
                <div
                  className="border border-border bg-card px-4 py-6 text-secondary-text text-center"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  Sem encomendas importadas para esta obra.
                </div>
              ) : (
                <WorkOrdersTab
                  workId={parsedWorkId}
                  orders={orders}
                  vatRate={work.vatRate}
                  suppliers={suppliers}
                  itemStatuses={itemStatuses}
                  onOrderChange={onOrderChange}
                  onOrderReplaced={(updated) =>
                    setOrders((prev) =>
                      prev.map((order) =>
                        order.id === updated.id ? updated : order,
                      ),
                    )
                  }
                  onCostImputed={() =>
                    setCostsRefreshKey((prev) => prev + 1)
                  }
                />
              ))}
            {/* O separador é renderizado mesmo sem linhas: a mensagem de vazio que aqui estava
                substituía o ecrã inteiro, e com ela desaparecia o botão de criar a primeira
                linha — que é precisamente o que falta a quem não tem nenhuma. */}
            {activeSubTab === "subempreiteiros" && (
              <WorkSubcontractorsTab
                workId={parsedWorkId}
                items={workSubcontractors}
                subcontractors={subcontractors}
                suppliers={suppliers}
                itemStatuses={itemStatuses}
                onItemChange={onSubcontractorChange}
                onItemReplaced={onSubcontractorReplaced}
                onItemCreated={onSubcontractorCreated}
                onCostImputed={() => setCostsRefreshKey((prev) => prev + 1)}
              />
            )}
            {activeSubTab === "subempreiteiros-autos" &&
              Number.isFinite(parsedWorkId) && (
                <WorkSubcontractorAutosTab
                  workId={parsedWorkId}
                  subcontractors={subcontractors}
                  suppliers={suppliers}
                  onLineCreated={onSubcontractorCreated}
                />
              )}
            {activeSubTab === "cronograma" &&
              Number.isFinite(parsedWorkId) && (
                <WorkScheduleTab workId={parsedWorkId} />
              )}
            {activeSubTab === "diario" &&
              Number.isFinite(parsedWorkId) && (
                <WorkDiaryTab workId={parsedWorkId} />
              )}
            {activeSubTab === "marcos" &&
              Number.isFinite(parsedWorkId) && (
                <WorkMilestonesTab workId={parsedWorkId} />
              )}
            {activeSubTab === "contratos" &&
              Number.isFinite(parsedWorkId) && (
                <EntityDocumentsSection
                  ownerType="work"
                  ownerId={parsedWorkId}
                  section="contract"
                  title="Contratos"
                  hideTitle
                  variant="card"
                  showCounterparty
                  categories={CONTRACT_CATEGORIES}
                  emptyLabel="Sem contratos arquivados."
                />
              )}
            {activeSubTab === "doc-tecnica" &&
              Number.isFinite(parsedWorkId) && (
                <EntityDocumentsSection
                  ownerType="work"
                  ownerId={parsedWorkId}
                  section="technical"
                  title="Documentação Técnica"
                  hideTitle
                  variant="card"
                  categories={TECHNICAL_CATEGORIES}
                  emptyLabel="Sem documentação técnica arquivada."
                />
              )}
            {activeSubTab === "arquivo-fotografico" &&
              Number.isFinite(parsedWorkId) && (
                <WorkPhotoArchiveTab workId={parsedWorkId} />
              )}
            {activeSubTab === "atas" &&
              Number.isFinite(parsedWorkId) && (
                <WorkMeetingMinutesTab workId={parsedWorkId} />
              )}
            {activeSubTab === "autos" &&
              Number.isFinite(parsedWorkId) && (
                <WorkMeasurementAutosTab workId={parsedWorkId} />
              )}
            {activeSubTab === "custos" &&
              Number.isFinite(parsedWorkId) && (
                <WorkCostsTab
                  workId={parsedWorkId}
                  refreshKey={costsRefreshKey}
                />
              )}
            {activeSubTab === "mapa-custos" &&
              Number.isFinite(parsedWorkId) && (
                <WorkCostMapTab
                  workId={parsedWorkId}
                  startDate={work.startDate}
                  endDate={work.endDate}
                  statusCode={work.statusCode}
                  statusName={work.statusName}
                  refreshKey={costsRefreshKey}
                />
              )}
            {activeSubTab === "variacoes" &&
              Number.isFinite(parsedWorkId) && (
                <WorkVariationsTab workId={parsedWorkId} />
              )}
            {activeSubTab === "financeiro" &&
              Number.isFinite(parsedWorkId) && (
                <WorkFinancialDocumentsTab
                  workId={parsedWorkId}
                  onCostImputed={() =>
                    setCostsRefreshKey((prev) => prev + 1)
                  }
                />
              )}
            {activeSubTab === "materiais" &&
              Number.isFinite(parsedWorkId) && (
                <WorkMaterialsTab workId={parsedWorkId} />
              )}
            {activeSubTab === "horas" &&
              Number.isFinite(parsedWorkId) && (
                <WorkLaborTab workId={parsedWorkId} />
              )}
            {activeSubTab === "dashboard" &&
              Number.isFinite(parsedWorkId) && (
                <WorkFinancialDashboardTab workId={parsedWorkId} />
              )}
            {activeSubTab === "balanco" &&
              Number.isFinite(parsedWorkId) && (
                <WorkPostMortemTab workId={parsedWorkId} />
              )}
            {activeSubTab === "pos-venda" &&
              Number.isFinite(parsedWorkId) && (
                <WorkAfterSalesTab workId={parsedWorkId} />
              )}
          </>
        )}
      </div>

      {isEditDrawerOpen && work && (
        <WorkEditDrawer
          initial={{
            clienteId,
            nomeObra,
            localObra,
            workStatusId,
            dataInicio,
            dataFim,
            valorTotal,
            valorGasto,
            progresso,
          }}
          clients={clients}
          statuses={workStatuses}
          isSaving={isSavingObra}
          error={saveObraError}
          onSave={handleSaveObra}
          onClose={() => setIsEditDrawerOpen(false)}
        />
      )}
    </div>
  );
}
